import { AxisCode } from '@atlas/types'
import { ListSkeleton } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { ArrowLeft } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/editorial/PageHeader'
import { isFromWorkfront } from '@/components/work-orders/WorkfrontBadges'
import type { Campaign } from '@/domain/campaign.types'
import { CAMPAIGN_LIST_MAX_LIMIT, NON_ARCHIVED_CAMPAIGN_STATUSES } from '@/domain/campaign.types'
import type { WorkOrder } from '@/domain/work-order.types'
import { useCampaigns } from '@/hooks/useCampaigns'
import { useBrandPath } from '@/hooks/useClassification'
import { useAxesWithValues, useFacetedOptions } from '@/hooks/useLibrary'
import { useActivePms, useUsers } from '@/hooks/useUsers'
import { useWorkOrderChildren } from '@/hooks/useWorkOrderExternal'
import { useCreateWorkOrder, useUpdateWorkOrder, useWorkOrder, useWorkOrders } from '@/hooks/useWorkOrders'
import { notification } from '@/notifications/notification-store'

import { BriefingCard, ClassificationCard, ContextCard, TitleCard } from './FormSections'
import { CodesCard, CostsFormCard, ExternalFormCard } from './FormSectionsCodes'
import {
	EMPTY_FORM,
	FACETED_CONTEXT_AXIS_CODES,
	facetedSelection,
	fromWorkOrder,
	lockedFieldsOf,
	toCreateInput,
	validateForm,
	type WorkOrderFormState,
} from './form-model'
import { toUpdateInput } from './form-model.update'
import { LinksCard } from './LinksCard'

/**
 * The campaign picker must not silently truncate — a campaign missing from the
 * list is indistinguishable from one that doesn't exist. Pinned to the largest
 * page the server will actually return: `buildListQuery` clamps `limit` to 100,
 * so asking for more yields 100 anyway while reading as a higher guarantee.
 * Revisit with `useCampaignsPages` once the live count approaches this.
 */
const CAMPAIGN_PICKER_LIMIT = CAMPAIGN_LIST_MAX_LIMIT

/** Who the Codes card picks from: the directory for the assignee, and every active PM for the PM. */
function usePickerPeople() {
	const { data: users = [] } = useUsers()
	const { data: pms = [] } = useActivePms()
	return { users, pms }
}

/**
 * The campaigns the picker offers: the non-archived list, plus the order's own
 * campaign when that list doesn't contain it. Without the fallback, an order
 * linked to an archived (or past-the-page) campaign renders as if it had none,
 * and saving would overwrite the real linkage with whatever the user picked
 * next. The second request only runs in that case.
 */
function usePickerCampaigns(selectedCode: string): Campaign[] {
	const { data: campaigns = [] } = useCampaigns(
		{ status: NON_ARCHIVED_CAMPAIGN_STATUSES },
		{ page: 1, limit: CAMPAIGN_PICKER_LIMIT },
	)
	const isMissing = !!selectedCode && !campaigns.some((c) => c.campaignCode === selectedCode)
	// Archived campaigns are excluded by status, so the fallback drops the status
	// filter entirely rather than naming ARCHIVED — a lifecycle state added later
	// is then covered without touching this.
	const { data: allStatuses = [] } = useCampaigns({}, { page: 1, limit: CAMPAIGN_PICKER_LIMIT }, { enabled: isMissing })
	if (!isMissing) return campaigns
	const match = allStatuses.find((c) => c.campaignCode === selectedCode)
	return match ? [match, ...campaigns] : campaigns
}

/**
 * The orders the parent picker offers: the board's first page, plus the order's own parent
 * when that page does not hold it. The page holds a handful of orders, so without the
 * fallback a child's parent renders as none, and a save could unlink it. The second request
 * only runs in that case.
 */
function usePickerParents(parentId: string | undefined): WorkOrder[] {
	const { data: orders = [] } = useWorkOrders()
	const isMissing = !!parentId && !orders.some((o) => o.workOrderId === parentId)
	const { data: parent } = useWorkOrder(isMissing ? (parentId ?? null) : null)
	return isMissing && parent ? [parent, ...orders] : orders
}

/**
 * Faceted options for the Context card. Region and brand go to the facet engine,
 * each narrowed by the other (skip-self keeps its own selection reachable), so
 * the counts beside a value describe the rest of the context. Language, species,
 * audience and channel are independent: absent from both the selection and the
 * axis list, so they cost no facet request.
 */
function useHierarchicalContextFacets(form: WorkOrderFormState) {
	const { optionsByAxis, isFetching } = useFacetedOptions(facetedSelection(form), [...FACETED_CONTEXT_AXIS_CODES], {
		keepNarrowedOutSelectionVisible: false,
	})

	// NOTHING here is auto-cleared, deliberately.
	//
	// A facet answers "what has been CLASSIFIED", and a new work order describes work that
	// does not exist yet: picking Bravecto narrows the geography facet to the markets some
	// file is already tagged with, which on a fresh corpus is none of them. Validating a
	// selection against that cleared the region the moment a brand was chosen — the author
	// is told their market is unavailable precisely because nobody has made the thing they
	// are asking for.
	//
	// The facet still drives what each picker OFFERS and how its counts read; it just no
	// longer decides what the author is allowed to have already said.

	return { contextOptionsByAxis: optionsByAxis, isFacetFetching: isFetching }
}

/** Shared presentational body — the grouped section cards + submit/cancel. */
function WorkOrderForm({
	form,
	setForm,
	heading,
	submitLabel,
	isPending,
	onSubmit,
	onCancel,
	showErrorsInitially = false,
	existing,
}: {
	form: WorkOrderFormState
	setForm: (updater: (f: WorkOrderFormState) => WorkOrderFormState) => void
	heading: { eyebrow: string; title: string; subtitle: string }
	submitLabel: string
	isPending: boolean
	onSubmit: () => void
	onCancel: () => void
	showErrorsInitially?: boolean
	/** The order being edited; absent on the New form. */
	existing?: WorkOrder
}) {
	const { axes, isLoading: isAxesLoading } = useAxesWithValues()
	const orders = usePickerParents(form.parentWorkOrderId)
	const people = usePickerPeople()
	const campaigns = usePickerCampaigns(form.campaignCode)
	// From the order's own children, not the board list: a board page holds only some orders,
	// so a parent whose children are elsewhere would offer costs the server refuses.
	const { data: childOrders = [] } = useWorkOrderChildren(existing?.workOrderId ?? null)
	const isParent = childOrders.length > 0
	// Validation messages surface only once the user has tried to submit, so a
	// pristine form isn't red before anything has been typed. The Edit page opts
	// in from the start — a legacy order missing a now-mandatory value must say
	// so on load rather than presenting an inert Save button.
	const [showErrors, setShowErrors] = useState(showErrorsInitially)
	// On an order from Workfront the fields the sync writes are read-only: a change made here
	// would be overwritten by the next sync.
	const locked = lockedFieldsOf(existing)
	const errors = validateForm(form, locked)
	const visibleErrors = showErrors ? errors : {}

	const set = <K extends keyof WorkOrderFormState>(key: K, value: WorkOrderFormState[K]) =>
		setForm((f) => ({ ...f, [key]: value }))
	const submit = (e: FormEvent) => {
		e.preventDefault()
		setShowErrors(true)
		if (Object.keys(errors).length === 0) onSubmit()
	}

	const { contextOptionsByAxis, isFacetFetching } = useHierarchicalContextFacets(form)

	return (
		<form onSubmit={submit} noValidate>
			<Link
				to="/work-orders"
				className="mb-4 inline-flex items-center gap-1 text-sm text-ds-text-muted hover:text-ds-text"
			>
				<ArrowLeft className="h-4 w-4" /> Work Orders
			</Link>
			<PageHeader eyebrow={heading.eyebrow} title={heading.title} subtitle={heading.subtitle} />

			<div className="mt-6 space-y-5">
				<TitleCard form={form} set={set} error={visibleErrors.title} isLocked={locked.has('title')} />

				{/* Description sits directly under the title (its own card). */}
				<BriefingCard form={form} set={set} isWorkfront={Boolean(existing && isFromWorkfront(existing))} />

				<ClassificationCard
					form={form}
					set={set}
					axes={axes}
					errors={visibleErrors}
					locked={locked}
					isLoading={isAxesLoading}
				/>
				<ContextCard
					form={form}
					set={set}
					axes={axes}
					campaigns={campaigns}
					contextOptionsByAxis={contextOptionsByAxis}
					isFacetFetching={isFacetFetching}
					isAxesLoading={isAxesLoading}
					locked={locked}
					storedTuple={existing?.tuple}
				/>
				<CodesCard form={form} set={set} orders={orders} {...people} errors={visibleErrors} locked={locked} />
				<CostsFormCard
					form={form}
					set={set}
					errors={visibleErrors}
					isParent={isParent}
					source={existing?.source ?? null}
					locked={locked}
				/>
				<ExternalFormCard order={existing} />
				<LinksCard form={form} set={set} />
			</div>

			<div className="mt-6 flex items-center gap-3">
				<Button type="submit" isDisabled={isPending} className="rounded-ds-full px-6">
					{isPending ? 'Saving…' : submitLabel}
				</Button>
				<Button type="button" variant="secondary" className="rounded-ds-full" onPress={onCancel}>
					Cancel
				</Button>
			</div>
		</form>
	)
}

/**
 * New Work Order — full-page form (route `/work-orders/new`). Dimension/attribute
 * pickers are registry-driven searchable Comboboxes; parent order is a real
 * column, not an axis. Title, complexity and asset type are required (plus a
 * duration for video asset types); the order code is generated server-side.
 */
export function NewWorkOrderPage() {
	const navigate = useNavigate()
	const create = useCreateWorkOrder()
	const [form, setForm] = useState<WorkOrderFormState>(EMPTY_FORM)

	return (
		<WorkOrderForm
			form={form}
			setForm={setForm}
			heading={{
				eyebrow: 'Work Orders / New',
				title: 'New work order',
				subtitle:
					'Every field is a dimension or attribute Atlas resolves context from. Title, complexity and asset type are required — the order code is generated on save.',
			}}
			submitLabel="Create work order"
			isPending={create.isPending}
			onSubmit={() =>
				create.mutate(toCreateInput(form), {
					onSuccess: () => void navigate('/work-orders'),
					// Without this a rejected save is entirely silent: no navigation,
					// no message, and a submit button that just returns to its idle
					// label — indistinguishable from a mis-click.
					onError: () => notification.error('The work order could not be created. Your changes are still here.'),
				})
			}
			onCancel={() => void navigate('/work-orders')}
		/>
	)
}

/** Edit Work Order — same form hydrated from an existing order, PATCH on save. */
export function EditWorkOrderPage() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { data: wo, isLoading } = useWorkOrder(id ?? null)
	const update = useUpdateWorkOrder(id)
	// The form as it loaded, kept beside the edited one: the save sends only what differs.
	const [loaded, setLoaded] = useState<WorkOrderFormState | null>(null)
	const [form, setForm] = useState<WorkOrderFormState | null>(null)
	const brandCodes = wo?.tuple[AxisCode.BRAND]
	const { path: brandPath, isLoading: isBrandPathLoading } = useBrandPath(brandCodes)

	// Hydrate once (guard so edits aren't clobbered on refetch), and only once the brand tags
	// can be split into Brand and Product — `!isBrandPathLoading` rather than `brandPath.length`,
	// since an empty path is ambiguous between "still resolving" and "no brand tag at all".
	useEffect(() => {
		if (wo && !form && (!brandCodes?.length || !isBrandPathLoading)) {
			const hydrated = fromWorkOrder(wo, brandPath)
			setLoaded(hydrated)
			setForm(hydrated)
		}
	}, [wo, form, brandCodes, brandPath, isBrandPathLoading])

	if (isLoading || !form || !loaded) return <ListSkeleton label="Loading…" />

	return (
		<WorkOrderForm
			form={form}
			setForm={setForm as (u: (f: WorkOrderFormState) => WorkOrderFormState) => void}
			heading={{
				eyebrow: `Work Orders / ${wo?.orderCode ?? 'Edit'}`,
				title: 'Edit work order',
				subtitle:
					wo && isFromWorkfront(wo)
						? 'This order comes from Workfront: the fields it sends are read-only here and are changed in Workfront. Changes save on submit.'
						: 'Update any field. Status moves stay on the board; changes save on submit.',
			}}
			submitLabel="Save changes"
			isPending={update.isPending}
			// An order created before complexity and asset type were mandatory must
			// say what is missing the moment it opens.
			showErrorsInitially
			existing={wo}
			onSubmit={() => {
				if (!id || !wo) return
				const patch = toUpdateInput(form, loaded, wo)
				// Nothing changed: there is nothing to save, so leave without a request.
				if (Object.keys(patch).length === 0) {
					void navigate(`/work-orders/${id}`)
					return
				}
				update.mutate(
					{ id, patch },
					{
						onSuccess: () => void navigate(`/work-orders/${id}`),
						onError: () => notification.error('The changes could not be saved. Your edits are still here.'),
					},
				)
			}}
			onCancel={() => void navigate(`/work-orders/${id}`)}
		/>
	)
}
